import React, { useState } from 'react';
import { Modal } from '../common/Modal';
import { FileUploadZone } from './shared/FileUploadZone';
import { ValidationErrors } from './shared/ValidationErrors';
import { WarningMessage } from './shared/WarningMessage';
import { validatePilgrimData } from '../../utils/pilgrimValidation';
import { PilgrimSchema } from '../../types/pilgrim';
import * as XLSX from 'xlsx';
import toast from 'react-hot-toast';
import { useLanguage } from '../../contexts/LanguageContext';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onUpdate: (data: PilgrimSchema[]) => Promise<{ message: string }>;
};

export const UpdatePilgrimsModal = ({ isOpen, onClose, onUpdate }: Props) => {
  const [file, setFile] = useState<File | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const { translations } = useLanguage();

  const validateFile = (file: File) => {
    const validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel'
    ];
    
    if (!validTypes.includes(file.type)) {
      toast.error(translations?.updatePilgrimsModal?.excelOnly || 'يجب اختيار ملف Excel فقط');
      return false;
    }
    
    return true;
  };

  const handleFileSelect = (file: File) => {
    if (validateFile(file)) {
      setFile(file);
      setValidationErrors([]);
    }
  };

  const readExcelFile = (file: File): Promise<PilgrimSchema[]> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          const data = e.target?.result;
          const workbook = XLSX.read(data, { type: 'binary' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet);
          resolve(jsonData as PilgrimSchema[]);
        } catch (error) {
          reject(error);
        }
      };
      
      reader.onerror = (error) => reject(error);
      reader.readAsBinaryString(file);
    });
  };

  const handleUpdate = async () => {
    if (!file) return;

    setIsLoading(true);
    setValidationErrors([]);

    try {
      const data = await readExcelFile(file);
      const allErrors: string[] = [];
      
      data.forEach((row, index) => {
        const { isValid, errors } = validatePilgrimData(row);
        if (!isValid) {
          allErrors.push(`صف ${index + 1}: ${errors.join(', ')}`);
        }
      });

      if (allErrors.length > 0) {
        setValidationErrors(allErrors);
        return;
      }

      const result = await onUpdate(data);
      toast.success(result.message);
      onClose();
    } catch (error: any) {
      console.error('Error updating pilgrims:', error);
      toast.error(error.message || translations?.updatePilgrimsModal?.updateError || 'حدث خطأ أثناء تحديث البيانات');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={translations?.updatePilgrimsModal?.title || 'تحديث بيانات الحجاج'}>
      <div className="space-y-6">
        <FileUploadZone
          file={file}
          onFileSelect={handleFileSelect}
          onFileRemove={() => {
            setFile(null);
            setValidationErrors([]);
          }}
        />

        <ValidationErrors errors={validationErrors} />
        <WarningMessage />

        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:text-gray-800"
          >
            {translations?.updatePilgrimsModal?.cancel || 'إلغاء'}
          </button>
          <button
            onClick={handleUpdate}
            disabled={!file || isLoading || validationErrors.length > 0}
            className={`px-6 py-2 rounded-lg text-white
              ${!file || isLoading || validationErrors.length > 0
                ? 'bg-gray-400 cursor-not-allowed' 
                : 'bg-primary-500 hover:bg-primary-600'}`}
          >
            {isLoading ? translations?.updatePilgrimsModal?.updating || 'جاري التحديث...' : translations?.updatePilgrimsModal?.update || 'تحديث البيانات'}
          </button>
        </div>
      </div>
    </Modal>
  );
};